import React, { useState, useEffect } from 'react';
import cn from 'classnames';
import { useDebounce } from 'react-use';
import LinkNext from 'next/link';
import NoSSR from 'react-no-ssr';
import useSystemTheme from 'react-use-system-theme';
import { Link } from 'react-scroll';

import InputSearch from 'react-scale/components/tailwind/Form/InputSearch';
import SearchResults from 'react-scale/components/pages/docs/SearchResults';
import CTAS from 'react-scale/components/elements/cta/buttons';

import { inputIconsList } from 'react-scale/components/icons/form/InputIcons';

import moment from 'moment';
import rdmd from '@readme/markdown';

import Close from 'react-scale/components/pages/docs/Close';

import Sidebar from 'react-scale/components/pages/docs/Sidebar';
import BodyContent from 'react-scale/components/pages/docs/BodyContent';
import IndexPage from 'react-scale/components/pages/docs/IndexPage';

import { scaleLogo } from './svg';

import styles from './Main.module.css';

const Main = ({
  topLevelNavigation,
  subLevelNavigation,
  title,
  content,
  slug,
  updated,
  index = false
}) => {
  const [searchItems, updateSearchItems] = useState([]);
  const [searchQuery, updatesearchQuery] = useState('');
  const [searchBox, updateSearchBox] = useState(false);

  const [openHeaderMobile, setOpenHeaderMobile] = useState(false);

  // eslint-disable-next-line no-empty-pattern
  const [] = useDebounce(
    async () => {
      if (!searchQuery.length) {
        updateSearchItems([]);
      } else {
        fetch(searchEndpoint(searchQuery))
          .then(res => res.json())
          .then(res => {
            updateSearchItems(res.results);
          });
      }
    },
    100,
    [searchQuery]
  );

  const listOnThisPage = rdmd(content)?.props.children.map(list => list.props);
  const filteredList = listOnThisPage?.filter(obj => obj?.id !== undefined);

  useEffect(() => {
    window.addEventListener('click', hideModal);
    return () => {
      window.removeEventListener('click', hideModal);
    };
  }, []);

  const searchEndpoint = query => `/api/content/docs?search=${query}`;

  const onSearch = query => {
    updatesearchQuery(query);
  };

  const handleModal = ev => {
    ev.stopPropagation();
    updateSearchBox(true);
  };

  const hideModal = () => {
    updateSearchBox(false);
  };

  const toggleHeaderMobile = () => {
    setOpenHeaderMobile(!openHeaderMobile);
  };

  return (
    <>
      <header className="sticky top-0 z-10 w-full border-b border-neutral-100 dark:border-neutral-800 backdrop-blur-sm bg-white/75 dark:bg-neutral-900/75">
        <div className="relative flex lg:justify-start mx-auto h-20 items-center max-w-[88rem] px-6 lg:px-8 xl:px-12">
          <div className="relative flex items-center justify-between w-full lg:w-80">
            <LinkNext href="/" className="-translate-y-1">
              {scaleLogo}
            </LinkNext>
            <div className="block lg:hidden">
              <Close handleClick={toggleHeaderMobile} />
            </div>
          </div>
          <div className="relative items-center justify-between flex-grow hidden lg:flex basis-0">
            <div className="w-72">
              <InputSearch
                icon={inputIconsList.search}
                color="text-neutral-700 dark:text-neutral-300"
                bg="bg-white dark:bg-neutral-900"
                placeholder="Start typing to search..."
                style={{ width: '100%' }}
                onSearch={ev => onSearch(ev.target.value)}
                onClick={ev => handleModal(ev)}
                allowDarkMode
              />
            </div>
            {searchBox && <SearchResults items={searchItems} query={searchQuery} />}
            <CTAS
              theme={useSystemTheme()}
              ctas={[
                {
                  text: 'Create an Account',
                  href: 'https://dashboard.scale.com/signup',
                  type: 'primary',
                  variant: useSystemTheme() === 'dark' ? 'default' : 'black'
                },
                {
                  text: 'Talk to Us',
                  href: '/sales',
                  type: 'secondary',
                  variant: useSystemTheme() === 'dark' ? 'default' : 'black'
                }
              ]}
              size="small"
            />
          </div>
        </div>
      </header>
      <div className="relative lg:flex justify-start mx-auto max-w-[88rem] py-8 lg:py-0 px-6 lg:px-8 xl:px-12">
        <Sidebar
          topLevelNavigation={topLevelNavigation}
          subLevelNavigation={subLevelNavigation}
          slug={slug}
          openHeaderMobile={openHeaderMobile}
        />
        <BodyContent>
          {!index ? (
            <>
              <div className="pb-6 text-4xl text-black font-aeonik dark:text-white">{title}</div>
              <div
                className={cn(
                  styles.content,
                  'prose dark:prose-invert prose-headings:font-normal prose-blockquote:font-normal prose-h1:text-xl prose-h1:mt-8 prose-h2:text-xl prose-h3:text-base prose-h1:font-medium prose-h2:font-medium prose-h3:font-medium prose-blockquote:text-sm prose-blockquote:py-1 prose-blockquote:rounded-lg prose-blockquote:not-italic max-w-none'
                )}
              >
                {rdmd(content)}
              </div>
              <div className="flex items-center pt-6 mt-12 text-sm border-t text-neutral-600 border-neutral-300 dark:text-neutral-300 dark:border-neutral-800">
                <span className="mr-2">{calendarIcon}</span>
                Updated {moment(updated).fromNow()}
              </div>
            </>
          ) : (
            <IndexPage />
          )}
        </BodyContent>
        <NoSSR>
          {!index && filteredList?.length > 1 && (
            <div>
              <div className="hidden flex-auto w-64 lg:sticky lg:top-[81px] lg:h-[calc(100vh-81px)] lg:py-14 overflow-y-auto lg:pl-14 lg:block">
                <h3 className="mb-2 text-sm font-medium text-black dark:text-white">
                  On this page
                </h3>
                <ul>
                  {filteredList?.map(item => (
                    <li className="mb-2 last:mb-0" key={item?.id}>
                      <Link
                        activeClass={styles.active}
                        className="relative text-sm font-normal duration-300 cursor-pointer text-zinc-600 hover:text-black dark:text-zinc-500 dark:hover:text-white transition-color"
                        to={item?.id}
                        smooth
                        spy
                        offset={-105}
                        duration={500}
                      >
                        {item?.children[1]}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </NoSSR>
      </div>
    </>
  );
};

export const calendarIcon = (
  <svg
    className="fill-current text-neutral-600 dark:text-neutral-300"
    width="24"
    height="24"
    viewBox="0 0 24 24"
  >
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M9 3.75V5.25C9 5.66421 8.66421 6 8.25 6C7.83579 6 7.5 5.66421 7.5 5.25V3.75C7.5 3.33579 7.83579 3 8.25 3C8.66421 3 9 3.33579 9 3.75ZM20.25 6V18.75C20.25 19.5784 19.5784 20.25 18.75 20.25H5.25C4.42157 20.25 3.75 19.5784 3.75 18.75V6C3.75 5.17157 4.42157 4.5 5.25 4.5H6.375C6.58211 4.5 6.75 4.66789 6.75 4.875V5.18558V5.18558C6.73686 5.97403 7.32017 6.6456 8.1027 6.74295C8.92727 6.82418 9.66155 6.22158 9.74277 5.39702C9.74759 5.34816 9.75 5.29909 9.75 5.25V4.875C9.75 4.66789 9.91789 4.5 10.125 4.5H13.875C14.0821 4.5 14.25 4.66789 14.25 4.875V5.18557V5.18557C14.2369 5.974 14.8201 6.64555 15.6026 6.74295C16.4272 6.8242 17.1615 6.22161 17.2428 5.39703C17.2476 5.34817 17.25 5.2991 17.25 5.25V4.875C17.25 4.66789 17.4179 4.5 17.625 4.5H18.75C19.5784 4.5 20.25 5.17157 20.25 6C20.25 6 20.25 6 20.25 6V6ZM18.375 10.5H5.625C5.41789 10.5 5.25 10.6679 5.25 10.875V18.375C5.25 18.5821 5.41789 18.75 5.625 18.75H18.375C18.5821 18.75 18.75 18.5821 18.75 18.375V10.875C18.75 10.6679 18.5821 10.5 18.375 10.5ZM15.75 3C16.1642 3 16.5 3.33579 16.5 3.75V5.25C16.5 5.66421 16.1642 6 15.75 6C15.3358 6 15 5.66421 15 5.25V3.75C15 3.33579 15.3358 3 15.75 3Z"
    />
  </svg>
);

export default Main;
